import { MuiButton } from '@/components';
import StorageProgressBar from '@/components/ProgressBar';
import { Box, Button, Divider, Grid, Theme, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SettingsLinks from './SettingsLink';
import PromoCodeField from './PromoCodeField';
import { useDispatch } from 'react-redux';
import { logout, openSubscriptionModal, resumeSubscription } from '@/store/actions';
import { fallbackRestUrl, logoutWithFacebook } from '@/utils';
import { useRouter } from 'next/router';

const SubscriptionSection = ({ user, manageSub, handleStartMembership, palette }: any) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const handleDeleteAccount = () => {
    router.push('/app/delete-account');
  };
  const handleSubscriptionCancel = () => {
    dispatch(openSubscriptionModal());
  };

  const handleRenewSubscription = () => {
    dispatch(resumeSubscription({ userId: user?.id }));
  };

  const handlePaymentHistory = async () => {
    try {
      const apiUrl = process.env.REST_URL || fallbackRestUrl;
      const response = await fetch(`${apiUrl}/stripe/create-billing-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.token}`,
        },
        body: JSON.stringify({ userId: user?.id }),
      });
      const data = await response.json();
      if (data?.result?.url) {
        window.location.href = data.result.url;
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
    }
  };
  const handleLogout = async () => {
    router.push('/app/register');
    dispatch(logout());
    await logoutWithFacebook();
  };
  return (
    <Grid item xs={isMobile ? 12 : 12} mb={0.6}>
      <Box display={'flex'} justifyContent={'space-between'}>
        <Box>
          <Typography fontSize={'1rem'} color={'#1D1D1D'}>
            Subscription
          </Typography>
          <Box display={'flex'} justifyContent={'space-between'}>
            {/* Show subscription status dynamically */}
            <Typography fontSize={'1rem'} color='#5C6372'>
              {user.isSubscriber
                ? user.subscriptionStatus === 'cancellation_scheduled'
                  ? `Cancelled${user.subscriptionPlanName ? ` - ${user.subscriptionPlanName}` : ''}`
                  : `Active${user.subscriptionPlanName ? ` - ${user.subscriptionPlanName}` : ''}`
                : user.hasSingleStoryPurchase
                ? 'Single Story Purchase'
                : 'Not Subscribed'}
            </Typography>
          </Box>
          {user.hasSingleStoryPurchase && !user.isSubscriber && (
            <Typography fontSize={'0.8rem'} color='#5C6372' mt={0.5}>
              Purchased: {new Date(user.singleStoryPurchaseDate).toLocaleDateString()}
            </Typography>
          )}
        </Box>
        <Box textAlign={'right'}>
          {user.isSubscriber ? (
            <>
              <Box>
                <Typography
                  variant='button'
                  sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                  color={'black'}
                  fontSize={'1rem'}>
                  <a onClick={manageSub}>Manage subscription</a>
                </Typography>
                <Typography sx={{ display: 'flex', gap: '0.4rem' }} variant='body2' color='#5C6372' fontSize={'0.8rem'}>
                  <Button sx={{ fontSize: '0.8rem', padding: '0', color: '#5C6372' }} onClick={handlePaymentHistory}>Payment history</Button>

                  {user.subscriptionStatus === 'cancellation_scheduled' ? (
                    <Button
                      sx={{
                        fontSize: '0.8rem',
                        padding: '0',
                        cursor: 'pointer',
                        color: '#5C6372',
                      }}
                      onClick={handleRenewSubscription}>
                      Renew subscription
                    </Button>
                  ) : (
                    <Button
                      sx={{
                        fontSize: '0.8rem',
                        padding: '0',
                        cursor: 'pointer',
                        color: '#5C6372',
                      }}
                      onClick={handleSubscriptionCancel}>
                      Cancel subscription
                    </Button>
                  )}
                </Typography>
              </Box>
            </>
          ) : (
            <MuiButton
              sx={{
                backgroundColor: 'white',
                color: 'black',
                borderRadius: '2rem',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'white',
                  boxShadow: 'none',
                },
              }}
              method={handleStartMembership}>
              <Typography variant='button' color={'#000'} fontSize={'0.8rem'}>
                Start membership
              </Typography>
            </MuiButton>
          )}
        </Box>
      </Box>

      {/* Promo code — only for an active subscription with an upcoming charge to discount */}
      {user.isSubscriber && user.subscriptionStatus !== 'cancellation_scheduled' && (
        <PromoCodeField user={user} />
      )}

      {/* Storage Usage Section */}
      {user?.usedStorage !== undefined && user?.totalStorage && (
        <Box mt={2}>
          <Typography fontSize={'0.9rem'} color={'#1D1D1D'} mb={1}>
            Storage Usage
          </Typography>
          <StorageProgressBar 
            usedStorage={user.usedStorage || 0} 
            totalStorage={user.totalStorage || 0}
          />
        </Box>
      )}

      {!isMobile && (
        <Divider
          style={{
            margin: '1rem 0',
            width: '100%',
            background: isMobile ? 'transparent' : 'rgba(216, 232, 246, 1)',
          }}
        />
      )}
      <SettingsLinks
        isMobile={isMobile}
        palette={palette}
        handleDeleteAccount={handleDeleteAccount}
        handleLogout={handleLogout}
      />
    </Grid>
  );
};

export default SubscriptionSection;
